import { Injectable, Injector } from '@angular/core';
import { find, get, isEqual, merge } from 'lodash';

import {
  BackendApiType,
  ENTITY_TYPES,
  IBaseEntityType,
  IDocumentOperationOptions,
  IEntityConfig,
  IErrorOverride,
  IFilterQuery,
  ISiteRequestBody,
  ISiteResponse,
} from '@app/shared/types';

import { BaseService } from '../base';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { MAX_PAGE_SIZE } from '@config/global-constants';
import { Observable, observableThrowError, rx } from '@app/shared/rx';
import { NO_PERMISSION_STATUS } from '@app/shared/constants';

const entityType: ENTITY_TYPES = ENTITY_TYPES.SITE;
const config: IEntityConfig = {
  backendApiType: BackendApiType.PORTAL,
  ENTITY_TYPE: entityType,
  entityIdPath: '_id',
  isMasterListAvailable: true,
  apiEndpoint: entityType,
  uiRoute: entityType,
  buttons: {},
};

@Injectable()
export class SitesService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;
  public entityPath: string = 'id';

  public constructor(public injector: Injector) {
    super(injector);
  }

  public getItemValue(item: IBaseEntityType): string {
    return get(item, this.entityPath, '');
  }

  public fetchList(
    userQuery?: IFilterQuery,
    options?: IDocumentOperationOptions,
  ): Observable<ISiteResponse> {
    return this.companyService.currentCompanyId$.pipe(
      rx.filter((companyId) => !!companyId),
      rx.switchMap((companyId) => {
        const basicPath = `v1/companies/${companyId}/get-sites`;
        const headers = this.composeHeadersForUpdateRequest({
          ...options,
          disallowBusinessDateHeaders: true,
        });
        const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
        const [requestQuery, requestBody] = this.getRequestParameters(query);
        const params: HttpParams = this.createParams({
          top: MAX_PAGE_SIZE,
          ...requestQuery,
        });

        return this.http
          .post(basicPath, requestBody, { params, headers })
          .pipe(this.catchHttpError()) as Observable<ISiteResponse>;
      }),
    );
  }

  /* eslint-disable */
  protected catchHttpError =
    (errorOverride?: IErrorOverride) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) => {
          const errorData = get(error, 'errors');
          const hasNoPermissionError = find(errorData, (errorItem) =>
            isEqual(errorItem.status, NO_PERMISSION_STATUS),
          );
          if (hasNoPermissionError) {
            return this.httpErrorService.catchNoPermissionHttpError(errorData);
          }

          return observableThrowError(errorData);
        }),
      );

  private getRequestParameters(
    query: IFilterQuery,
  ): [IFilterQuery, ISiteRequestBody] {
    const { select } = query;
    const needKey = ['id', 'name', 'store_number', 'metadata'];

    query.select = select ? select : needKey;

    let requestBody: ISiteRequestBody;
    const searchQuery: IFilterQuery = { ...query };
    const odataFilter = get(searchQuery, 'filter');

    if (odataFilter) {
      delete searchQuery.filter;
      requestBody = {
        'x-odata-filter': `$filter=${odataFilter}`,
      };
    }

    return [searchQuery, requestBody];
  }
}
